import React, { useEffect, useState, useContext, useMemo } from "react";
import { DataGrid, GridFilterInputDate } from "@mui/x-data-grid";
import { Container, Typography, Box, CircularProgress, Alert, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, MenuItem, Select, InputLabel, FormControl } from "@mui/material";
import { API_ENDPOINTS, API_BASE_URL } from "../config/api.config";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import { format } from 'date-fns';
import { UserContext } from '../components/MiniDrawer';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Tooltip as MuiTooltip } from '@mui/material';
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { parseLocalDate } from '../utils/dateHelpers';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import Chip from '@mui/material/Chip';
import { getAbsences, deleteAbsence } from '../utils/absenceService';

function getColumnsFromData(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  // Colonne principali flat secondo il nuovo tracciato
  return [
    { field: 'project_name', headerName: 'Project', width: 180 },
    { field: 'ticket_id', headerName: 'Ticket ID', width: 140 },
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 130,
      type: 'date',
      filterable: true,
      valueFormatter: (params) => {
        if (!params.value) return '';
        // Fix timezone: parseLocalDate interpreta la data come locale
        const date = parseLocalDate(params.value);
        return date.toLocaleDateString('en-GB'); // Format as DD/MM/YYYY
      },
      filterOperators: [
        {
          label: 'Equals',
          value: 'equals',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return value === filterItem.value;
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'After',
          value: 'after',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              // Fix timezone: parseLocalDate interpreta la data come locale
              return parseLocalDate(value) > parseLocalDate(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'Before',
          value: 'before',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              // Fix timezone: parseLocalDate interpreta la data come locale
              return parseLocalDate(value) < parseLocalDate(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
      ],
    },
    { 
      field: 'effort', 
      headerName: 'Effort', 
      width: 100,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const hours = Math.round((Number(params.value) / 60) * 100) / 100;
        return `${hours} h`;
      }
    },
    { field: 'activity_name', headerName: 'Activity', width: 180 },
    { field: 'status_name', headerName: 'Status', width: 140 },
    { field: 'effort_type_name', headerName: 'Effort Type', width: 140 },
    { field: 'user_name', headerName: 'User', width: 180 },
    { field: 'description', headerName: 'Description', width: 250 },
  ];
}

export default function MonthlyUserView() {
  const { selectedUser } = useContext(UserContext);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({});
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [effortTypes, setEffortTypes] = useState([]);
  const [absenceDays, setAbsenceDays] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Filtri per mese/anno - legge dai parametri URL se presenti
  const now = new Date();
  const urlMonth = searchParams.get('month');
  const urlYear = searchParams.get('year');
  const [filterMonth, setFilterMonth] = useState(
    urlMonth ? parseInt(urlMonth) : now.getMonth() + 1
  ); // 1-12
  const [filterYear, setFilterYear] = useState(
    urlYear ? parseInt(urlYear) : now.getFullYear()
  );
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const yearOptions = [];
  for (let y = 2020; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

  // Aggiorna i filtri quando cambiano i parametri URL
  useEffect(() => {
    const urlMonth = searchParams.get('month');
    const urlYear = searchParams.get('year');
    if (urlMonth) setFilterMonth(parseInt(urlMonth));
    if (urlYear) setFilterYear(parseInt(urlYear));
  }, [searchParams]);

  // Sposto fetchWorklogs fuori da useEffect
  const fetchWorklogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = selectedUser === 'ALL'
        ? API_ENDPOINTS.WORKLOGS
        : `${API_ENDPOINTS.WORKLOGS}?user_id=${selectedUser}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Error fetching data");
      const data = await response.json();
      const arr = data.worklogs || [];
      setRows((selectedUser === 'ALL' ? arr : arr.filter(row => String(row.user_id || row.user?.id) === String(selectedUser))).map((row, idx) => ({ id: row.id || idx, ...row })));
      setColumns(getColumnsFromData(arr));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedUser) return;
    fetchWorklogs();
  }, [selectedUser]);

  // Modifico il filtro dei dati
  const filteredRows = rows.filter(row => {
    let match = true;
    if (row.date) {
      // Fix timezone: parseLocalDate interpreta la data come locale
      const d = parseLocalDate(row.date);
      match = match && (d.getMonth() + 1 === Number(filterMonth)) && (d.getFullYear() === Number(filterYear));
    }
    return match;
  });

  // Ricava i campi editabili (escludi id, created_at, updated_at)
  const getEditableFields = () => {
    if (!columns.length) return [];
    return columns.filter(col => !['id', 'created_at', 'updated_at'].includes(col.field));
  };

  // Gestione apertura dialog
  const handleOpenDialog = (row = null) => {
    setEditingRow(row);
    setFormData(row ? { ...row } : { user: selectedUser });
    setOpenDialog(true);
    // Fetch users
    fetch(API_ENDPOINTS.USERS)
      .then(res => res.json())
      .then(data => {
        let usersArr = data.users || [];
        setUsers(Array.isArray(usersArr) ? usersArr : []);
      })
      .catch(() => setUsers([]));
    // Fetch activities
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then(res => res.json())
      .then(data => {
        let arr = data.activities || [];
        setActivities(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setActivities([]));
    // Fetch status
    fetch(API_ENDPOINTS.STATUS)
      .then(res => res.json())
      .then(data => {
        let arr = data.status || [];
        setStatusList(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setStatusList([]));
    // Fetch effort types
    fetch(API_ENDPOINTS.EFFORT_TYPES)
      .then(res => res.json())
      .then(data => {
        let arr = data.effort_types || [];
        setEffortTypes(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setEffortTypes([]));
  };

  // Rimuovo la funzione handleExportExcel

  // Azioni e colonne
  const statusCol = columns.find(col => col.field === 'status_name');
  const otherCols = columns.filter(col => col.field !== 'status_name');
  const actionsColumn = {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    renderCell: (params) => (
      <Box>
        {params.row.status_name === 'Approved' ? (
          <Tooltip title="Blocked">
            <span>
              <IconButton disabled style={{ color: '#bdbdbd' }}>
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Edit">
            <span>
              <IconButton onClick={() => handleOpenDialog(params.row)}>
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {params.row.status_name === 'Approved' ? (
          <Tooltip title="Blocked">
            <span>
              <IconButton disabled style={{ color: '#bdbdbd' }}>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Delete">
            <span>
              <IconButton onClick={() => handleDelete(params.row.id)}>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    )
  };
  const columnsWithActions = statusCol
    ? [actionsColumn, ...otherCols, statusCol]
    : [actionsColumn, ...otherCols];

  // Tooltip per la colonna Description
  const columnsWithDescriptionTooltip = columnsWithActions.map(col => {
    if (col.field === 'description') {
      return {
        ...col,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} placement="top" arrow>
            <span style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              width: '100%'
            }}>{params.value}</span>
          </Tooltip>
        )
      };
    }
    return col;
  });

  // Dialog helpers
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRow(null);
    setFormData({});
  };
  const handleFormChange = (e, field) => {
    if (e && e.target) {
      setFormData({ ...formData, [field]: e.target.value });
    }
  };
  const handleSave = async () => {};
  const handleDelete = async (id) => {};

  // Stato per la tabella pivot
  const [pivotData, setPivotData] = useState([]);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [pivotError, setPivotError] = useState(null);
  const [pivotMode, setPivotMode] = useState('user'); // 'user' o 'project'
  // Stato per dettagli celle
  const [pivotDetails, setPivotDetails] = useState({});

  // Calcola giorni del mese selezionato
  const daysInMonth = useMemo(() => {
    return new Date(filterYear, filterMonth, 0).getDate();
  }, [filterYear, filterMonth]);

  // Dimensioni fisse per tabella responsive con scroll orizzontale
  const tableDimensions = useMemo(() => {
    const firstColWidth = 140; // px
    const dayColWidth = 45; // px per ciascun giorno
    const totalColWidth = 60; // px
    const minWidth = firstColWidth + daysInMonth * dayColWidth + totalColWidth;
    return { firstColWidth, dayColWidth, totalColWidth, minWidth };
  }, [daysInMonth]);

  // Calcola i nomi dei giorni della settimana per il mese selezionato
  const weekDays = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      return date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0);
    });
  }, [filterYear, filterMonth, daysInMonth]);

  // Calcola indici dei weekend per il mese selezionato
  const weekendIndexes = useMemo(() => {
    return weekDays.map((wd, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      const day = date.getDay(); // 0=Sunday, 6=Saturday
      return (day === 0 || day === 6);
    });
  }, [weekDays, filterYear, filterMonth]);

  useEffect(() => {
    if (!selectedUser || selectedUser === 'ALL') { setAbsenceDays([]); return; }
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    getAbsences(selectedUser, yearMonth).then(setAbsenceDays).catch(() => setAbsenceDays([]));
  }, [selectedUser, filterMonth, filterYear]);

  // Fetch pivot data ogni volta che cambia mese/anno o utente
  useEffect(() => {
    setPivotLoading(true);
    setPivotError(null);
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const userId = selectedUser === 'ALL' ? '' : selectedUser;
    if (!userId) {
      setPivotData({});
      setPivotLoading(false);
      return;
    }
    const url = `${API_BASE_URL}/monthly_worklog_summary_user_project?user_id=${userId}&year_month=${yearMonth}`;
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(data => {
        // data: { data: [ { project_name, work_date, total_effort, ... } ] }
        const byProject = {};
        if (Array.isArray(data.data)) {
          data.data.forEach(item => {
            const project = item.project_name || (item.project && item.project.name) || 'Unknown Project';
            // Fix timezone: parseLocalDate interpreta la data come locale
            const day = parseLocalDate(item.work_date).getDate();
            if (!byProject[project]) byProject[project] = {};
            // Se esiste già un worklog per questo giorno, aggrega i total_effort
            if (byProject[project][day]) {
              const existing = byProject[project][day];
              byProject[project][day] = {
                ...existing,
                total_effort: (Number(existing.total_effort) || 0) + (Number(item.total_effort) || 0),
                // Mantieni i dettagli del primo worklog trovato per il tooltip
                ticket_id: existing.ticket_id || item.ticket_id,
                description: existing.description || item.description,
                activity_name: existing.activity_name || item.activity_name,
                status_name: existing.status_name || item.status_name,
                effort_type_name: existing.effort_type_name || item.effort_type_name
              };
            } else {
              // Mantieni l'oggetto completo invece di solo il numero
              byProject[project][day] = item;
            }
          });
        }
        setPivotData(byProject);
      })
      .catch(err => setPivotError('Error loading data'))
      .finally(() => setPivotLoading(false));
  }, [filterMonth, filterYear, selectedUser]);

  // Calcola totali per riga e per giorno (basati sui dati filtrati, convertiti da minuti a ore)
  const rowTotals = useMemo(() => {
    const totals = {};
    Object.entries(pivotData).forEach(([rowKey, days]) => {
      totals[rowKey] = Math.round((Object.values(days).reduce((sum, obj) => sum + (Number(obj?.total_effort) || 0), 0) / 60) * 100) / 100;
    });
    return totals;
  }, [pivotData]);
  const dayTotals = useMemo(() => {
    const totals = {};
    for (let d = 1; d <= daysInMonth; d++) {
      totals[d] = Math.round((Object.values(pivotData).reduce((sum, days) => sum + (Number(days[d]?.total_effort) || 0), 0) / 60) * 100) / 100;
    }
    return totals;
  }, [pivotData, daysInMonth]);
  const grandTotal = useMemo(() => Math.round(Object.values(rowTotals).reduce((sum, v) => sum + v, 0) * 100) / 100, [rowTotals]);

  // Funzione per esportare i worklog in Excel
  const handleExportExcel = async () => {
    try {
      // Costruisce l'URL per il fetch dei worklog
      const url = selectedUser === 'ALL'
        ? API_ENDPOINTS.WORKLOGS
        : `${API_ENDPOINTS.WORKLOGS}?user_id=${selectedUser}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error("Error fetching data for export");
      const data = await response.json();
      const allWorklogs = data.worklogs || [];
      
      // Filtra i worklog per il mese/anno selezionato
      const filteredWorklogs = allWorklogs.filter(row => {
        if (!row.date) return false;
        // Fix timezone: parseLocalDate interpreta la data come locale
        const d = parseLocalDate(row.date);
        return (d.getMonth() + 1 === Number(filterMonth)) && (d.getFullYear() === Number(filterYear));
      });

      if (filteredWorklogs.length === 0) {
        alert('No data to export for the selected period');
        return;
      }

      // Prepara i dati per l'Excel
      const excelData = filteredWorklogs.map(row => ({
        'Project': row.project_name || '',
        'Ticket ID': row.ticket_id || '',
        'Date': row.date ? parseLocalDate(row.date).toLocaleDateString('en-GB') : '',
        'Effort in minutes': row.effort || '',
        'Activity': row.activity_name || '',
        'Status': row.status_name || '',
        'Effort Type': row.effort_type_name || '',
        'User': row.user_name || '',
        'Description': row.description || ''
      }));

      // Crea il workbook e il worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Aggiunge il worksheet al workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Worklogs');
      
      // Genera il file Excel
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Genera il nome del file
      const monthName = monthNames[filterMonth - 1];
      const fileName = `Worklogs_${monthName}_${filterYear}.xlsx`;
      
      // Scarica il file
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting data to Excel');
    }
  };

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>Monthly User View</Typography>
          <Button variant="outlined" color="primary" onClick={handleExportExcel} startIcon={<DownloadIcon />}>
            Excel
          </Button>
        </Box>
        {/* Filtri mese e anno */}
        <Box 
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-start' }}
          mb={2}
        >
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel id="filter-month-label">Month</InputLabel>
            <Select
              labelId="filter-month-label"
              value={filterMonth}
              label="Month"
              onChange={e => setFilterMonth(Number(e.target.value))}
            >
              {monthNames.map((name, idx) => (
                <MenuItem key={idx + 1} value={idx + 1}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 100 }} size="small">
            <InputLabel id="filter-year-label">Year</InputLabel>
            <Select
              labelId="filter-year-label"
              value={filterYear}
              label="Year"
              onChange={e => setFilterYear(Number(e.target.value))}
            >
              {yearOptions.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" onClick={() => { setFilterMonth(now.getMonth() + 1); setFilterYear(now.getFullYear()); }}>Reset</Button>
        </Box>
        {absenceDays.length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff8f5', borderRadius: 2, border: '1px solid #ffccbc', display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
              <EventBusyIcon sx={{ color: '#e53935', fontSize: '1.1rem' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#bf360c' }}>Absence days:</Typography>
            </Box>
            {absenceDays.sort((a, b) => a.date.localeCompare(b.date)).map(v => (
              <Chip
                key={v.id}
                label={`${parseLocalDate(v.date).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' })} (${v.full_day ? 'full' : 'partial'})`}
                size="small"
                sx={{ bgcolor: v.full_day ? '#ef9a9a' : '#fff176', color: v.full_day ? '#b71c1c' : '#f57f17', fontWeight: 500, '& .MuiChip-deleteIcon': { color: v.full_day ? 'rgba(183,28,28,0.6)' : 'rgba(245,127,23,0.6)', '&:hover': { color: v.full_day ? '#b71c1c' : '#f57f17' } } }}
                onDelete={async () => {
                  await deleteAbsence(v.id);
                  const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
                  getAbsences(selectedUser, yearMonth).then(setAbsenceDays).catch(() => setAbsenceDays([]));
                }}
              />
            ))}
          </Box>
        )}
        {pivotLoading ? (
          <CircularProgress />
        ) : pivotError ? (
          <Alert severity="error">{pivotError}</Alert>
        ) : (Object.keys(pivotData).length === 0 && absenceDays.length === 0) ? (
          <Alert severity="info">No worklogs or absences found for the selected user and date period.</Alert>
        ) : (
          <TableContainer component={Paper} sx={{ 
            borderRadius: 2, 
            bgcolor: 'white', 
            p: 0, 
            maxWidth: '100vw', 
            overflowX: 'auto', 
            boxShadow: 'none', 
            border: '1px solid #f3f3f3'
          }}>
            <Table size="small" sx={{ 
              minWidth: tableDimensions.minWidth, 
              tableLayout: 'fixed', 
              fontSize: '0.85rem', 
              width: '100%', 
              bgcolor: 'white'
            }}>
              <colgroup>
                <col style={{ width: tableDimensions.firstColWidth }} />
                {Array.from({ length: daysInMonth }).map((_, i) => (
                  <col key={i} style={{ width: tableDimensions.dayColWidth }} />
                ))}
                <col style={{ width: tableDimensions.totalColWidth }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell
                    className="pivot-user-col"
                    sx={{
                      bgcolor: '#5ab031',
                      border: 'none',
                      p: 0,
                      minWidth: 120,
                      maxWidth: 160,
                      width: 140,
                      color: 'white',
                      fontWeight: 'normal',
                      borderRight: '0px solid #e0e0e0',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }}
                    rowSpan={2}
                  >
                    Project
                  </TableCell>
                  {weekDays.map((wd, i) => {
                    const isWeekend = weekendIndexes[i];
                    return (
                      <TableCell key={i} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{wd}</TableCell>
                    );
                  })}
                  <TableCell sx={{ 
                    bgcolor: '#5ab031', 
                    color: 'white', 
                    fontWeight: 'normal', 
                    fontSize: '0.85rem', 
                    p: 0.5, 
                    border: '0px solid #f3f3f3', 
                    position: 'sticky', 
                    right: 0, 
                    zIndex: 3, 
                    verticalAlign: 'middle', 
                    textAlign: 'center',
                    minWidth: 60,
                    maxWidth: 80,
                    width: 60,
                    '@media (max-width: 900px)': {
                      position: 'sticky',
                      right: 0,
                      zIndex: 3,
                      boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                    }
                  }} rowSpan={2}>Tot</TableCell>
                </TableRow>
                <TableRow>
                  {/* Tolgo la cella vuota della prima colonna */}
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const isWeekend = weekendIndexes[i];
                    return (
                      <TableCell key={i+1} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{i+1}</TableCell>
                    );
                  })}
                  {/* Nessuna TableCell qui per Tot, perché la cella sopra ha rowSpan=2 */}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(pivotData).length === 0 && absenceDays.length > 0 ? (
                  <TableRow
                    key="__absences__"
                    hover
                    sx={{
                      '&:hover': { bgcolor: '#e6f4ea' }
                    }}
                  >
                    <TableCell 
                      className="pivot-user-col"
                      sx={{ 
                        bgcolor: 'white', 
                        color: '#5ab031',
                        position: 'sticky', 
                        left: 0, 
                        zIndex: 2,
                        minWidth: 120, 
                        maxWidth: 160, 
                        width: 140, 
                        p: 0.5, 
                        border: '1px solid #f3f3f3', 
                        borderRight: '2px solid #5ab031', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        textAlign: 'left',
                        fontWeight: 'normal'
                      }}
                    >
                      Absences
                    </TableCell>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const isWeekend = weekendIndexes[i];
                      const day = i + 1;
                      const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const absEntry = absenceDays.find(v => v.date === dateStr);
                      return (
                        <TableCell
                          key={i + 1}
                          sx={{
                            textAlign: 'center',
                            bgcolor: absEntry ? (absEntry.full_day ? '#ffcdd2 !important' : '#fff9c4 !important') : isWeekend ? 'rgba(55,55,55,0.08)' : 'white',
                            color: '#374151',
                            fontSize: '0.7rem',
                            p: 0.5,
                            border: '1px solid #f3f3f3',
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontVariantNumeric: 'tabular-nums',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: absEntry ? (absEntry.full_day ? '#ef9a9a !important' : '#fff176 !important') : '#aace7c78' }
                          }}
                          onClick={() => navigate(`/daily-tracking?date=${dateStr}`)}
                        >
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ 
                      bgcolor: 'white !important', 
                      color: 'black !important', 
                      textAlign: 'center', 
                      fontSize: '0.7rem', 
                      p: 0.5, 
                      border: '1px solid #f3f3f3', 
                      borderLeft: '2px solid #5ab031', 
                      position: 'sticky', 
                      right: 0, 
                      zIndex: 2,
                      minWidth: 60,
                      maxWidth: 80,
                      width: 60,
                      fontWeight: 'bold'
                    }}>
                      {absenceDays.length}
                    </TableCell>
                  </TableRow>
                ) : Object.entries(pivotData).map(([rowKey, days]) => (
                    <TableRow
                      key={rowKey}
                      hover
                      sx={{
                        '&:hover': { bgcolor: '#e6f4ea' }
                      }}
                    >
                      <MuiTooltip title={`${rowKey}`} arrow>
                        <TableCell 
                          className="pivot-user-col"
                          sx={{ 
                            bgcolor: 'white', 
                            color: '#5ab031',
                            position: 'sticky', 
                            left: 0, 
                            zIndex: 2,
                            minWidth: 120, 
                            maxWidth: 160, 
                            width: 140, 
                            p: 0.5, 
                            border: '1px solid #f3f3f3', 
                            borderRight: '2px solid #5ab031', 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontWeight: 'normal',
                            textDecoration: 'underline',
                            '&:hover': {
                              bgcolor: '#e6f4ea',
                              color: '#4a9b2a',
                              fontWeight: 'bold'
                            },
                            '@media (max-width: 900px)': {
                              position: 'sticky',
                              left: 0,
                              zIndex: 2,
                              boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          {rowKey}
                        </TableCell>
                      </MuiTooltip>
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const obj = days[i + 1];
                        const value = obj?.total_effort ? Math.round((Number(obj.total_effort) / 60) * 100) / 100 : '';
                        const isWeekend = weekendIndexes[i];
                        const day = i + 1;
                        const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const absEntry = absenceDays.find(v => v.date === dateStr);
                        return (
                          <TableCell key={i + 1} sx={{ textAlign: 'center', bgcolor: absEntry ? (absEntry.full_day ? '#ffcdd2 !important' : '#fff9c4 !important') : isWeekend ? 'rgba(55,55,55,0.08)' : 'white', color: '#374151', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', fontWeight: 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums', cursor: 'pointer', '&:hover': { bgcolor: absEntry ? (absEntry.full_day ? '#ef9a9a !important' : '#fff176 !important') : '#aace7c78' } }} onClick={() => navigate(`/daily-tracking?date=${dateStr}`)}>
                            {obj ? (
                              <MuiTooltip
                                title={
                                  <span>
                                    {obj.ticket_id && <div><b>Ticket:</b> {obj.ticket_id}</div>}
                                    {obj.description && <div><b>Desc:</b> {obj.description}</div>}
                                    {obj.activity_name && <div><b>Activity:</b> {obj.activity_name}</div>}
                                    {obj.status_name && <div><b>Status:</b> {obj.status_name}</div>}
                                    {obj.effort_type_name && <div><b>Effort Type:</b> {obj.effort_type_name}</div>}
                                    {obj.total_effort && <div><b>Effort:</b> {Math.round((Number(obj.total_effort) / 60) * 100) / 100} h</div>}
                                  </span>
                                }
                                arrow
                              >
                                <span>{value}</span>
                              </MuiTooltip>
                            ) : value}
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ 
                        bgcolor: 'white !important', 
                        color: 'black !important', 
                        textAlign: 'center', 
                        fontSize: '0.7rem', 
                        p: 0.5, 
                        border: '1px solid #f3f3f3', 
                        borderLeft: '2px solid #5ab031', 
                        position: 'sticky', 
                        right: 0, 
                        zIndex: 2,
                        minWidth: 60,
                        maxWidth: 80,
                        width: 60,
                        fontWeight: 'bold',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          right: 0,
                          zIndex: 2,
                          boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}>{rowTotals[rowKey]}</TableCell>
                    </TableRow>
                  ))}
                {/* Riga totale per giorno (ripristinata) */}
                <TableRow sx={{ bgcolor: '#248b7e' }}>
                  <TableCell 
                      sx={{  
                        bgcolor: 'white', 
                        position: 'sticky', 
                        left: 0, 
                        zIndex: 2, 
                        minWidth: 120, 
                        maxWidth: 160, 
                        width: 140, 
                        p: 0.5, 
                        border: '1px solid #f3f3f3', 
                        borderRight: '2px solid #5ab031', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        textAlign: 'right', 
                        borderTop: '2px solid #5ab031',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }} 
                    className="pivot-user-col"
                  >
                    Tot
                  </TableCell>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const dayTotal = dayTotals[i+1];
                    const isOver8Hours = dayTotal > 8;
                    
                    return (
                      <Tooltip 
                        key={i+1}
                        title={isOver8Hours ? `Warning: ${dayTotal} hours exceeds the recommended 8-hour daily limit` : `${dayTotal} hours`}
                        arrow
                      >
                        <TableCell 
                          sx={{ 
                            bgcolor: isOver8Hours ? '#fdeded' : 'white !important', 
                            color: 'black !important', 
                            textAlign: 'center', 
                            fontSize: '0.7rem', 
                            p: 0.5, 
                            border: '1px solid #f3f3f3', 
                            borderTop: '2px solid #5ab031', 
                            fontWeight: 'bold'
                          }}
                        >
                          {dayTotal}
                        </TableCell>
                      </Tooltip>
                    );
                  })}
                    <TableCell sx={{ 
                      bgcolor: 'white !important', 
                      color: 'black !important', 
                      textAlign: 'center', 
                      fontSize: '0.7rem', 
                      p: 0.5, 
                      border: '1px solid #f3f3f3', 
                      borderLeft: '2px solid #5ab031', 
                      borderTop: '2px solid #5ab031', 
                      position: 'sticky', 
                      right: 0, 
                      zIndex: 2,
                      minWidth: 60,
                      maxWidth: 80,
                      width: 60,
                      fontWeight: 'bold',
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        right: 0,
                        zIndex: 2,
                        boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }}>{grandTotal}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* Note about effort in hours */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          mt: 1, 
          pr: 1 
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#666', 
              fontSize: '0.75rem',
              fontStyle: 'italic'
            }}
          >
            * effort in hours
          </Typography>
        </Box>
      </Box>
    </Box>
  );
} 